const COOKIE_PREFIX = 'eating_admin_'
const STORAGE_META_KEY = 'eating_admin_session_meta'
const STORAGE_LOGOUT_REASON_KEY = 'eating_admin_logout_reason'
/** 24 小时未操作 → 自动退出 */
const INACTIVE_TIMEOUT_MS = 24 * 60 * 60 * 1000
/** 自登录起最长 15 天 → 强制重新登录 */
const MAX_SESSION_MS = 15 * 24 * 60 * 60 * 1000
/** Cookie 需覆盖最长会话，避免未到 15 天就丢 Token */
const COOKIE_DAYS = 15
let activityTrackingInited = false
let activityTimer = null
let lastTouchMs = 0

function setCookie(name, value, days = COOKIE_DAYS) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${COOKIE_PREFIX}${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`
}

function getCookie(name) {
  const key = `${COOKIE_PREFIX}${name}=`
  const parts = document.cookie ? document.cookie.split('; ') : []
  const found = parts.find(p => p.startsWith(key))
  if (!found) return null
  return decodeURIComponent(found.slice(key.length))
}

function removeCookie(name) {
  document.cookie = `${COOKIE_PREFIX}${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
}

function readSessionMeta() {
  try {
    const raw = localStorage.getItem(STORAGE_META_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (_e) {
    return null
  }
}

function writeSessionMeta(meta) {
  try {
    localStorage.setItem(STORAGE_META_KEY, JSON.stringify(meta || {}))
  } catch (_e) {
    // ignore storage failure
  }
}

function clearSessionMeta() {
  try {
    localStorage.removeItem(STORAGE_META_KEY)
  } catch (_e) {
    // ignore storage failure
  }
}

function setLogoutReason(reason) {
  const s = String(reason || '').trim()
  if (!s) return
  try {
    sessionStorage.setItem(STORAGE_LOGOUT_REASON_KEY, s)
  } catch (_e) {
    // ignore storage failure
  }
}

function nowMs() {
  return Date.now()
}

function getSessionState() {
  const isLoggedIn = getCookie('isLoggedIn') === 'true'
  const token = getCookie('adminToken')
  if (!isLoggedIn || !token) {
    return { valid: false, reason: 'missing_auth' }
  }

  const meta = readSessionMeta()
  const now = nowMs()
  if (!meta) {
    // 旧会话无 meta：补写起点，避免立刻踢出；仍受后续空闲/最长规则约束
    writeSessionMeta({
      rememberMe: false,
      issuedAt: now,
      expiresAt: now + MAX_SESSION_MS,
      idleTimeoutMs: INACTIVE_TIMEOUT_MS,
      lastActiveAt: now
    })
    return { valid: true, reason: '' }
  }

  const issuedAt = Number(meta.issuedAt || 0)
  const expiresAt = Number(meta.expiresAt || 0)
  const lastActiveAt = Number(meta.lastActiveAt || issuedAt || 0)
  const hardExpire = expiresAt > 0 ? expiresAt : issuedAt > 0 ? issuedAt + MAX_SESSION_MS : 0

  // 15 天强制退出（自登录起）
  if (hardExpire > 0 && now >= hardExpire) {
    return { valid: false, reason: 'expired_15d' }
  }
  if (issuedAt > 0 && now - issuedAt >= MAX_SESSION_MS) {
    return { valid: false, reason: 'expired_15d' }
  }

  // 24 小时未操作自动退出
  if (lastActiveAt > 0 && now - lastActiveAt > INACTIVE_TIMEOUT_MS) {
    return { valid: false, reason: 'inactive_24h' }
  }
  return { valid: true, reason: '' }
}

export function setAuthSession({ userInfo, isLoggedIn, token, rememberMe }) {
  const now = nowMs()
  const keep = !!rememberMe
  setCookie('isLoggedIn', isLoggedIn ? 'true' : 'false', COOKIE_DAYS)
  setCookie('userInfo', JSON.stringify(userInfo || {}), COOKIE_DAYS)
  if (token != null && String(token).trim() !== '') {
    setCookie('adminToken', String(token).trim(), COOKIE_DAYS)
  } else {
    removeCookie('adminToken')
  }
  writeSessionMeta({
    rememberMe: keep,
    issuedAt: now,
    expiresAt: now + MAX_SESSION_MS,
    idleTimeoutMs: INACTIVE_TIMEOUT_MS,
    lastActiveAt: now
  })
}

export function clearAuthSession(reason = '') {
  removeCookie('isLoggedIn')
  removeCookie('userInfo')
  removeCookie('adminToken')
  clearSessionMeta()
  setLogoutReason(reason)
}

export function getAdminToken() {
  return getCookie('adminToken')
}

/** 供独立 axios 调用（与 $api 一致） */
export function getAdminAuthHeader() {
  const t = getAdminToken()
  return t ? { Authorization: `Bearer ${t}` } : {}
}

export function getIsLoggedIn() {
  const state = getSessionState()
  if (!state.valid) {
    clearAuthSession(state.reason)
    return false
  }
  return true
}

export function getUserInfo() {
  const raw = getCookie('userInfo')
  if (!raw) return {}
  try {
    return JSON.parse(raw)
  } catch (e) {
    return {}
  }
}

/** 合并更新本地会话中的 userInfo（不改 token / 登录态） */
export function patchUserInfo(partial) {
  const prev = getUserInfo() || {}
  const next = Object.assign({}, prev, partial || {})
  const meta = readSessionMeta() || {}
  const keep = !!meta.rememberMe
  const issuedAt = Number(meta.issuedAt || Date.now())
  setCookie('userInfo', JSON.stringify(next), COOKIE_DAYS)
  writeSessionMeta({
    rememberMe: keep,
    issuedAt,
    expiresAt: Number(meta.expiresAt || issuedAt + MAX_SESSION_MS),
    idleTimeoutMs: INACTIVE_TIMEOUT_MS,
    lastActiveAt: Number(meta.lastActiveAt || Date.now())
  })
  return next
}

/** 员工、收银员：后台禁止删除（与接口 DELETE 403 一致，用于隐藏按钮） */
export function canAdminDelete() {
  const r = String(getUserInfo().roleLabel || '').trim()
  return r !== '员工' && r !== '收银员'
}

export function isSuperAdminUser() {
  return String(getUserInfo().roleLabel || '').trim() === '超级管理员'
}

export function isStaffOrCashierUser() {
  const r = String(getUserInfo().roleLabel || '').trim()
  return r === '员工' || r === '收银员'
}

export function touchSessionActivity() {
  const meta = readSessionMeta()
  if (!meta) return
  const now = nowMs()
  if (now - lastTouchMs < 1000) return
  meta.lastActiveAt = now
  writeSessionMeta(meta)
  lastTouchMs = now
}

export function ensureAuthSessionValid({ touch = false } = {}) {
  const state = getSessionState()
  if (!state.valid) {
    clearAuthSession(state.reason)
    return state
  }
  if (touch) touchSessionActivity()
  return state
}

export function consumeLogoutReason() {
  try {
    const r = sessionStorage.getItem(STORAGE_LOGOUT_REASON_KEY) || ''
    sessionStorage.removeItem(STORAGE_LOGOUT_REASON_KEY)
    return r
  } catch (_e) {
    return ''
  }
}

function redirectToLoginIfNeeded() {
  if (typeof window === 'undefined') return
  if (String(window.location.hash || '').includes('#/login')) return
  window.location.hash = '#/login'
}

export function initAuthActivityTracking() {
  if (activityTrackingInited || typeof window === 'undefined') return
  activityTrackingInited = true

  const onActivity = () => touchSessionActivity()
  ;['click', 'keydown', 'mousemove', 'touchstart', 'scroll'].forEach((evt) => {
    window.addEventListener(evt, onActivity, { passive: true })
  })

  activityTimer = window.setInterval(() => {
    const state = getSessionState()
    if (state.valid) return
    if (
      state.reason === 'inactive_24h' ||
      state.reason === 'expired_15d' ||
      state.reason === 'inactive_30m' ||
      state.reason === 'expired_24h'
    ) {
      clearAuthSession(state.reason)
      redirectToLoginIfNeeded()
    }
  }, 10000)
}
